// Modern Art - Premium Design System
// Inspired by luxury board game interfaces and modern web design trends

// ============================================================================
// GAME CONSTANTS (never change with theme)
// These colors represent game mechanics and should stay consistent
// ============================================================================
export const GAME_COLORS = {
  // Artist color schemes - represent the 5 artists, always consistent
  // These are the ORIGINAL baseline colors from engine/constants.ts
  artists: {
    'Solstice Vane': {
      primary: '#F5C846', // Original yellow/gold
      secondary: '#fbbf24',
      accent: '#fcd34d',
      gradient: 'linear-gradient(135deg, #F5C846 0%, #fbbf24 100%)',
      glow: 'rgba(245, 200, 70, 0.3)',
    },
    'Mira Faux': {
      primary: '#2DD4BF', // Original teal
      secondary: '#14b8a6',
      accent: '#5eead4',
      gradient: 'linear-gradient(135deg, #2DD4BF 0%, #14b8a6 100%)',
      glow: 'rgba(45, 212, 191, 0.3)',
    },
    'Atlas Zero': {
      primary: '#DC2626', // Original red
      secondary: '#b91c1c',
      accent: '#f87171',
      gradient: 'linear-gradient(135deg, #DC2626 0%, #b91c1c 100%)',
      glow: 'rgba(220, 38, 38, 0.3)',
    },
    'Vesper Kint': {
      primary: '#22C55E', // Original green
      secondary: '#16a34a',
      accent: '#4ade80',
      gradient: 'linear-gradient(135deg, #22C55E 0%, #16a34a 100%)',
      glow: 'rgba(34, 197, 94, 0.3)',
    },
    'Luxe Nebula': {
      primary: '#A855F7', // Original purple
      secondary: '#9333ea',
      accent: '#c084fc',
      gradient: 'linear-gradient(135deg, #A855F7 0%, #9333ea 100%)',
      glow: 'rgba(168, 85, 247, 0.3)',
    },
  },

  // Auction type colors - game mechanics, should be consistent
  auctionTypes: {
    open: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    one_offer: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    hidden: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    fixed_price: 'linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)',
    double: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
  },

  // Status colors - feedback indicators, should be consistent
  status: {
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
} as const

// ============================================================================
// LEGACY COLORS (for backwards compatibility during migration)
// TODO: Remove once migration is complete
// ============================================================================
export const colors = {
  // Primary brand palette - deep, rich colors
  primary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },

  // Luxury accents
  accent: {
    gold: '#fbbf24',
    goldDark: '#f59e0b',
    silver: '#e5e7eb',
    bronze: '#d97706',
    crystal: '#f0f9ff',
  },

  // Premium gradients
  gradients: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    luxury: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    ocean: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    sunset: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    forest: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    midnight: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    aurora: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)',
    // Dark mode gradients
    darkLuxury: 'linear-gradient(135deg, #1a1c20 0%, #2d3436 100%)',
    darkOcean: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
    royal: 'linear-gradient(135deg, #141e30 0%, #243b55 100%)',
  },

  // Artist color schemes (duplicated from GAME_COLORS for compatibility)
  artists: GAME_COLORS.artists,

  // Auction type colors (duplicated from GAME_COLORS for compatibility)
  auctionTypes: GAME_COLORS.auctionTypes,

  // Neutral palette with warm undertones
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },

  // Status colors (duplicated from GAME_COLORS for compatibility)
  status: GAME_COLORS.status,
}

// Typography system
export const typography = {
  fontFamily: {
    display: '"Playfair Display", "Georgia", serif',
    body: '"Inter", "Helvetica Neue", sans-serif',
    mono: '"Fira Code", "Monaco", monospace',
  },

  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
  },

  fontWeight: {
    thin: 100,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },

  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
}

// Spacing system
export const spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem',
  40: '10rem',
  48: '12rem',
  56: '14rem',
  64: '16rem',
}

// Advanced shadows and effects
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',

  // Premium colored shadows
  glow: {
    primary: '0 0 20px rgba(99, 102, 241, 0.3)',
    gold: '0 0 30px rgba(251, 191, 36, 0.4)',
    success: '0 0 20px rgba(16, 185, 129, 0.3)',
    danger: '0 0 20px rgba(239, 68, 68, 0.3)',
  },

  // Card-specific shadows
  card: {
    rest: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    hover: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    selected: '0 0 30px rgba(99, 102, 241, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
}

// Animation settings
export const animations = {
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
    slower: '1000ms',
  },

  easing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },

  // Keyframe definitions
  keyframes: {
    float: 'animation: float 3s ease-in-out infinite;',
    pulse: 'animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;',
    spin: 'animation: spin 1s linear infinite;',
    bounce: 'animation: bounce 1s infinite;',
    fadeIn: 'animation: fadeIn 0.5s ease-in-out;',
    slideUp: 'animation: slideUp 0.5s ease-out;',
    shimmer: 'animation: shimmer 2s linear infinite;',
    glow: 'animation: glow 2s ease-in-out infinite alternate;',
  },
}

// Breakpoints
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
}

// Border radius
export const borderRadius = {
  none: '0',
  sm: '0.125rem',
  base: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '3xl': '1.5rem',
  full: '9999px',
}

// Z-index scale
export const zIndex = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
}

// ============================================================================
// THEME TYPE DEFINITIONS
// ============================================================================
export type Theme = 'classic' | 'sunset' | 'cosmic'

/**
 * Artist color scheme - represents one of the 5 artists in the game
 */
export interface ArtistColors {
  primary: string
  secondary: string
  accent: string
  gradient: string
  glow: string
}

/**
 * Theme colors - All UI colors that change based on theme selection.
 * This includes both UI chrome AND unique artist color palettes for each theme.
 */
export interface ThemeColors {
  // Primary accent - the main brand color for the theme
  primary: string
  primaryHover: string
  primaryMuted: string

  // Backgrounds
  background: string           // Page background
  card: string                 // Card/panel background
  cardHover: string

  // Text
  text: string
  textSecondary: string
  textMuted: string

  // Borders & overlays
  border: string
  borderHover: string
  overlay: string

  // Interactive elements
  buttonPrimary: string
  buttonPrimaryHover: string
  buttonSecondary: string
  buttonSecondaryHover: string

  // Game UI chrome
  roundIndicator: string
  phaseBadge: string
  activePlayer: string
  shadow: string

  // Artist colors - unique 5-color palette for this theme
  artists: {
    'Solstice Vane': ArtistColors
    'Mira Faux': ArtistColors
    'Atlas Zero': ArtistColors
    'Vesper Kint': ArtistColors
    'Luxe Nebula': ArtistColors
  }
}

// ============================================================================
// THEME DEFINITIONS
// ============================================================================
export const themes: Record<Theme, ThemeColors> = {
  // --------------------------------------------------------------------------
  // CLASSIC - The Default Theme
  // Uses the original vibrant GAME_COLORS palette
  // Traditional board game aesthetic with clear visual distinction
  // --------------------------------------------------------------------------
  classic: {
    // Primary accent - yellow/gold from Solstice Vane
    primary: '#F5C846',
    primaryHover: '#fbbf24',
    primaryMuted: 'rgba(245, 200, 70, 0.15)',

    // Backgrounds - clean, neutral canvas
    background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
    card: 'rgba(255, 255, 255, 0.06)',
    cardHover: 'rgba(255, 255, 255, 0.12)',

    // Text
    text: '#fafafa',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    textMuted: 'rgba(255, 255, 255, 0.45)',

    // Borders
    border: 'rgba(255, 255, 255, 0.1)',
    borderHover: 'rgba(255, 255, 255, 0.18)',
    overlay: 'rgba(0, 0, 0, 0.6)',

    // Buttons
    buttonPrimary: 'linear-gradient(135deg, #F5C846 0%, #fbbf24 100%)',
    buttonPrimaryHover: 'linear-gradient(135deg, #fbbf24 0%, #fcd34d 100%)',
    buttonSecondary: 'rgba(255, 255, 255, 0.08)',
    buttonSecondaryHover: 'rgba(255, 255, 255, 0.15)',

    // Game UI
    roundIndicator: '#F5C846',
    phaseBadge: 'rgba(245, 200, 70, 0.15)',
    activePlayer: 'rgba(245, 200, 70, 0.25)',
    shadow: '0 0 35px rgba(245, 200, 70, 0.35)',

    // Classic Artist Palette - Original vibrant colors (from GAME_COLORS)
    artists: GAME_COLORS.artists,
  },

  // --------------------------------------------------------------------------
  // SUNSET - Warm, Vibrant, Energetic
  // Golden hour aesthetic - fiery, passionate, inviting
  // Rich warm palette from coral sunset to amber glow
  // Creates excitement and energy while maintaining elegance
  // --------------------------------------------------------------------------
  sunset: {
    // Primary accent - vibrant coral
    primary: '#FF6B6B',
    primaryHover: '#FF8787',
    primaryMuted: 'rgba(255, 107, 107, 0.15)',

    // Backgrounds - deep purple to warm sunset gradient
    background: 'linear-gradient(180deg, #1a0a2e 0%, #2d1b3d 50%, #3d1f2e 100%)',
    card: 'rgba(255, 150, 100, 0.06)',
    cardHover: 'rgba(255, 150, 100, 0.12)',

    // Text
    text: '#fff5eb',
    textSecondary: 'rgba(255, 230, 200, 0.75)',
    textMuted: 'rgba(255, 200, 150, 0.5)',

    // Borders
    border: 'rgba(255, 180, 120, 0.15)',
    borderHover: 'rgba(255, 180, 120, 0.25)',
    overlay: 'rgba(26, 10, 46, 0.7)',

    // Buttons
    buttonPrimary: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
    buttonPrimaryHover: 'linear-gradient(135deg, #FF8787 0%, #ffa06b 100%)',
    buttonSecondary: 'rgba(255, 180, 120, 0.1)',
    buttonSecondaryHover: 'rgba(255, 180, 120, 0.18)',

    // Game UI
    roundIndicator: '#FF6B6B',
    phaseBadge: 'rgba(255, 107, 107, 0.2)',
    activePlayer: 'rgba(255, 180, 100, 0.3)',
    shadow: '0 0 35px rgba(255, 107, 107, 0.4)',

    // Sunset Artist Palette - Five distinct warm colors spanning the color wheel
    artists: {
      'Solstice Vane': {
        primary: '#FFD93D', // Golden yellow
        secondary: '#FFC107',
        accent: '#FFE082',
        gradient: 'linear-gradient(135deg, #FFD93D 0%, #FFC107 100%)',
        glow: 'rgba(255, 217, 61, 0.35)',
      },
      'Mira Faux': {
        primary: '#FF6B35', // Vibrant orange
        secondary: '#E85D25',
        accent: '#FF8C5A',
        gradient: 'linear-gradient(135deg, #FF6B35 0%, #E85D25 100%)',
        glow: 'rgba(255, 107, 53, 0.32)',
      },
      'Atlas Zero': {
        primary: '#E63946', // Rich red
        secondary: '#D62839',
        accent: '#F75D6B',
        gradient: 'linear-gradient(135deg, #E63946 0%, #D62839 100%)',
        glow: 'rgba(230, 57, 70, 0.3)',
      },
      'Vesper Kint': {
        primary: '#06D6A0', // Warm teal
        secondary: '#05C090',
        accent: '#2FE8BF',
        gradient: 'linear-gradient(135deg, #06D6A0 0%, #05C090 100%)',
        glow: 'rgba(6, 214, 160, 0.28)',
      },
      'Luxe Nebula': {
        primary: '#9D4EDD', // Deep purple
        secondary: '#8B3DD1',
        accent: '#B769FF',
        gradient: 'linear-gradient(135deg, #9D4EDD 0%, #8B3DD1 100%)',
        glow: 'rgba(157, 78, 221, 0.3)',
      },
    },
  },

  // --------------------------------------------------------------------------
  // COSMIC - Cool, Mysterious, Elegant
  // Deep space aesthetic - otherworldly, sophisticated, immersive
  // Five distinct cosmic colors spanning nebula to starlight
  // Creates a sense of mystery and cosmic wonder
  // --------------------------------------------------------------------------
  cosmic: {
    // Primary accent - electric cyan
    primary: '#00D9FF',
    primaryHover: '#38E7FF',
    primaryMuted: 'rgba(0, 217, 255, 0.15)',

    // Backgrounds - deep space nebula gradient
    background: 'linear-gradient(180deg, #0a0e1a 0%, #0f1428 30%, #1a102e 70%, #0d0f1a 100%)',
    card: 'rgba(100, 180, 255, 0.05)',
    cardHover: 'rgba(100, 180, 255, 0.1)',

    // Text
    text: '#e8f4ff',
    textSecondary: 'rgba(160, 200, 255, 0.7)',
    textMuted: 'rgba(120, 170, 230, 0.45)',

    // Borders
    border: 'rgba(80, 150, 255, 0.15)',
    borderHover: 'rgba(100, 170, 255, 0.25)',
    overlay: 'rgba(5, 8, 20, 0.75)',

    // Buttons
    buttonPrimary: 'linear-gradient(135deg, #00D9FF 0%, #7B68EE 100%)',
    buttonPrimaryHover: 'linear-gradient(135deg, #38E7FF 0%, #9D8DF1 100%)',
    buttonSecondary: 'rgba(100, 160, 255, 0.08)',
    buttonSecondaryHover: 'rgba(100, 160, 255, 0.15)',

    // Game UI
    roundIndicator: '#00D9FF',
    phaseBadge: 'rgba(0, 217, 255, 0.15)',
    activePlayer: 'rgba(123, 104, 238, 0.25)',
    shadow: '0 0 35px rgba(0, 217, 255, 0.35)',

    // Cosmic Artist Palette - Five distinct cosmic colors
    artists: {
      'Solstice Vane': {
        primary: '#00D9FF', // Electric cyan
        secondary: '#00B4D8',
        accent: '#48E5F2',
        gradient: 'linear-gradient(135deg, #00D9FF 0%, #00B4D8 100%)',
        glow: 'rgba(0, 217, 255, 0.35)',
      },
      'Mira Faux': {
        primary: '#00F5D4', // Mint green
        secondary: '#00E5C4',
        accent: '#5DFFE8',
        gradient: 'linear-gradient(135deg, #00F5D4 0%, #00E5C4 100%)',
        glow: 'rgba(0, 245, 212, 0.3)',
      },
      'Atlas Zero': {
        primary: '#7B2CBF', // Deep violet
        secondary: '#6A24A8',
        accent: '#9D4EDD',
        gradient: 'linear-gradient(135deg, #7B2CBF 0%, #6A24A8 100%)',
        glow: 'rgba(123, 44, 191, 0.32)',
      },
      'Vesper Kint': {
        primary: '#FF006E', // Neon magenta
        secondary: '#E60064',
        accent: '#FF338A',
        gradient: 'linear-gradient(135deg, #FF006E 0%, #E60064 100%)',
        glow: 'rgba(255, 0, 110, 0.35)',
      },
      'Luxe Nebula': {
        primary: '#FEE440', // Starlight yellow
        secondary: '#EDD934',
        accent: '#FFF066',
        gradient: 'linear-gradient(135deg, #FEE440 0%, #EDD934 100%)',
        glow: 'rgba(254, 228, 64, 0.3)',
      },
    },
  },
}

// Game-specific dimensions
export const gameDimensions = {
  card: {
    width: {
      sm: '60px',
      md: '80px',
      lg: '120px',
      xl: '160px',
    },
    height: {
      sm: '90px',
      md: '120px',
      lg: '180px',
      xl: '240px',
    },
    aspectRatio: '2/3',
  },

  board: {
    maxWidth: '1200px',
    padding: '2rem',
  },

  hand: {
    spacing: '0.5rem',
    fanAngle: '30deg',
  },
}

// Export default theme
export const defaultTheme = {
  ...themes.classic,
  colors,
  typography,
  spacing,
  shadows,
  animations,
  borderRadius,
  zIndex,
}